import React, { useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import jsPDF from "jspdf";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import autoTable from "jspdf-autotable";
import { statusLabels } from "../utils/statusLabels";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Box,
  Button,
  Typography,
  Stack,
  Tooltip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  Divider,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";

dayjs.extend(utc);
dayjs.extend(timezone);

const defaultColumns = [
  { id: "secret_code", label: "Code" },
  { id: "sender_name", label: "Expéditeur" },
  { id: "receiver_name", label: "Destinataire" },
  { id: "amount", label: "Montant" },
  { id: "fee", label: "Commission" },
  { id: "status", label: "Statut" },
  { id: "sender_agency_name", label: "Agence" },
  { id: "sender_location_name", label: "Provenance" },
  { id: "receiver_location_name", label: "Destination" },
  { id: "created_by_name", label: "Agent" },
  { id: "created_at", label: "Date" },
  { id: "actions", label: "Actions" },
];

const fmtMoney = (value, currency = "") => {
  const n = Number(value ?? 0);
  const num = Number.isFinite(n) ? n : 0;
  return `${num.toFixed(2)}${currency ? ` ${currency}` : ""}`;
};

const getCurrencyFromTransactions = (transactions = []) => {
  const found = (transactions || []).find((t) => t?.currency)?.currency;
  return found || "";
};

const getStatusChipProps = (status) => {
  const s = (status || "").toLowerCase();

  if (s === "partially_paid") {
    return { label: "Partiellement payé", color: "info", variant: "filled" };
  }
  if (s.includes("pending") || s.includes("en") || s.includes("attente")) {
    return { label: "En-attente", color: "warning", variant: "filled" };
  }
  if (s === "paid" || s.includes("retir")) {
    return { label: "Retiré", color: "success", variant: "filled" };
  }
  if (s.includes("cancel") || s.includes("annul")) {
    return { label: "Annulé", color: "error", variant: "filled" };
  }
  return {
    label: statusLabels[status] || status || "—",
    color: "default",
    variant: "outlined",
  };
};

const formatTxDate = (value, reportTimezone = "Africa/Kinshasa") => {
  if (!value) return "—";
  try {
    return dayjs.utc(value).tz(reportTimezone).format("DD/MM/YYYY HH:mm");
  } catch {
    return dayjs(value).format("DD/MM/YYYY HH:mm");
  }
};

const TransactionTable = ({
  transactions = [],
  visibleCols = {},
  onToggleColumn = () => {},
  showColumnToggles = false,
  onToggleShowColumnToggles = () => {},
  reportTimezone = "Africa/Kinshasa",
  onViewDetails,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const printRef = useRef();

  const reportCurrency = useMemo(
    () => getCurrencyFromTransactions(transactions),
    [transactions]
  );

  const totals = useMemo(() => {
    let amount = 0;
    let fee = 0;

    (transactions || []).forEach((tx) => {
      amount += Number(tx?.amount ?? 0) || 0;
      fee += Number(tx?.fee ?? 0) || 0;
    });

    const grandTotal = amount + fee;
    return { amount, fee, grandTotal };
  }, [transactions]);

  const goToDetails = useCallback(
    (transactionOrSecretCode) => {
      const transaction =
        typeof transactionOrSecretCode === "string"
          ? transactions.find((tx) => tx.secret_code === transactionOrSecretCode)
          : transactionOrSecretCode;

      if (transaction && typeof onViewDetails === "function") {
        onViewDetails(transaction);
        return;
      }

      const secretCode =
        typeof transactionOrSecretCode === "string"
          ? transactionOrSecretCode
          : transactionOrSecretCode?.secret_code;

      if (!secretCode) return;

      const returnTo = location.pathname + location.search;
      navigate(`/transactions/${secretCode}`, { state: { returnTo } });
    },
    [location.pathname, location.search, navigate, onViewDetails, transactions]
  );

  const handleDownloadPDF = useCallback(() => {
    if (!printRef.current) return;

    const pdf = new jsPDF("l", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();

    pdf.setFontSize(16);
    pdf.text("DIEU EST NOTRE FORCE", pageWidth / 2, 14, { align: "center" });
    pdf.setFontSize(12);
    pdf.text("Rapport de Transaction", pageWidth / 2, 21, { align: "center" });
    pdf.setFontSize(10);
    pdf.text(`Date: ${dayjs().format("DD/MM/YYYY HH:mm")}`, pageWidth / 2, 27, {
      align: "center",
    });
    pdf.text(`Fuseau du rapport: ${reportTimezone}`, 10, 32);

    pdf.setFontSize(12);
    pdf.text(
      `Total Montant: ${fmtMoney(totals.amount, reportCurrency)}   |   Total Commission: ${fmtMoney(
        totals.fee,
        reportCurrency
      )}   |   Total Général: ${fmtMoney(totals.grandTotal, reportCurrency)}`,
      10,
      39
    );

    const table = printRef.current.querySelector("table");
    if (!table) return;

    const visibleHeaderCells = Array.from(table.querySelectorAll("thead tr th"))
      .map((th) => th.innerText.trim());

    const headers = visibleHeaderCells.filter((h) => h.toLowerCase() !== "actions");

    const data = Array.from(table.querySelectorAll("tbody tr")).map((tr) => {
      const cells = Array.from(tr.querySelectorAll("td")).map((td) => td.innerText.trim());
      if (visibleCols.actions) {
        return cells.slice(0, headers.length);
      }
      return cells;
    });

    autoTable(pdf, {
      head: [headers],
      body: data,
      startY: 44,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 2.2, valign: "middle" },
      headStyles: { fontSize: 10, fontStyle: "bold" },
      margin: { left: 6, right: 6 },
    });

    pdf.save(`transactions_${dayjs().format("DD-MM-YYYY_HHmm")}.pdf`);
  }, [
    reportCurrency,
    reportTimezone,
    totals.amount,
    totals.fee,
    totals.grandTotal,
    visibleCols.actions,
  ]);

  const handlePrint = useCallback(() => {
    if (!printRef.current) return;

    const printContent = printRef.current.innerHTML;
    const win = window.open("", "", "height=1000,width=1400");

    win.document.write(`
      <html>
        <head>
          <title>Transaction Rapport</title>
          <style>
            @page { size: A4 landscape; margin: 12mm; }
            body {
              font-family: Arial, sans-serif;
              padding: 8px 10px;
              color: #222;
              font-size: 14px;
              line-height: 1.35;
            }
            h2 { text-align:center; margin:0 0 6px 0; font-size: 22px; font-weight: 800; }
            h4 { text-align:center; margin:2px 0; font-size: 14px; font-weight: 700; }
            .totals { margin-top: 10px; font-size: 15px; font-weight: 800;text-align:left; }
            .tz { margin-top: 4px; font-size: 13px; font-weight: 700; }
            table {
              border-collapse: collapse;
              width: 100%;
              margin-top: 10px;
              font-size: 12px;
            }
            th, td {
              border: 1px solid #666;
              padding: 8px 10px;
              text-align: left;
              vertical-align: middle;
              white-space: nowrap;
            }
            th {
              background: #f1f3f5;
              font-weight: 800;
            }
            tr:nth-child(even) td { background-color: #fafafa; }
            @media print {
              body { -webkit-print-color-adjust: exact !important; }
              tr { page-break-inside: avoid; }
            }
            th:last-child, td:last-child { display: none; }
          </style>
        </head>
        <body>
          <h2>DIEU EST NOTRE FORCE</h2>
          <h4>Rapport de Transaction</h4>
          <h4>${dayjs().format("DD/MM/YYYY HH:mm")}</h4>
          <div class="tz">Fuseau du rapport: ${reportTimezone}</div>
          <div class="totals">
            Total Montant: ${fmtMoney(totals.amount, reportCurrency)} &nbsp;&nbsp;|&nbsp;&nbsp;
            Total Commission: ${fmtMoney(totals.fee, reportCurrency)} &nbsp;&nbsp;|&nbsp;&nbsp;
            Total Général: ${fmtMoney(totals.grandTotal, reportCurrency)}
          </div>
          <div>
            ${printContent}
          </div>
        </body>
      </html>
    `);

    win.document.close();
    win.focus();
    win.print();
  }, [reportCurrency, reportTimezone, totals.amount, totals.fee, totals.grandTotal]);

  const hasData = (transactions || []).length > 0;

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ minWidth: 260 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: "text.secondary", mb: 1, fontWeight: 700 }}
          >
            Résumé
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "space-between", fontWeight: 700, mb: 0.5 }}>
            <Typography>Total Transfer</Typography>
            <Typography>{fmtMoney(totals.amount, reportCurrency)}</Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", fontWeight: 700, mb: 0.5 }}>
            <Typography>Total Commission</Typography>
            <Typography>{fmtMoney(totals.fee, reportCurrency)}</Typography>
          </Box>

          <Divider sx={{ my: 0.7 }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: 900,
              fontSize: 18,
              color: "primary.main",
            }}
          >
            <Typography sx={{ fontWeight: 900 }}>Total Général</Typography>
            <Typography sx={{ fontWeight: 900 }}>
              {fmtMoney(totals.grandTotal, reportCurrency)}
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Button
            startIcon={<DownloadIcon />}
            variant="contained"
            onClick={handleDownloadPDF}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            Export PDF
          </Button>
          <Button
            startIcon={<PrintIcon />}
            variant="outlined"
            onClick={handlePrint}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            Imprimer
          </Button>
        </Stack>
      </Box>

      <Divider />

      <Box sx={{ px: 2, py: 1 }}>
        <Button variant="text" onClick={onToggleShowColumnToggles} sx={{ fontWeight: 700 }}>
          {showColumnToggles ? "Masquer les Colonnes" : "Afficher les Colonnes"}
        </Button>
      </Box>

      {showColumnToggles && (
        <Box sx={{ px: 2, pb: 2 }}>
          <FormGroup row>
            {defaultColumns.map((col) => (
              <FormControlLabel
                key={col.id}
                control={
                  <Checkbox
                    checked={!!visibleCols[col.id]}
                    onChange={() => onToggleColumn(col.id)}
                  />
                }
                label={col.label}
              />
            ))}
          </FormGroup>
        </Box>
      )}

      <div ref={printRef}>
        <TableContainer sx={{ maxHeight: 640 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {defaultColumns.map(
                  (col) =>
                    visibleCols[col.id] && (
                      <TableCell
                        key={col.id}
                        sx={{
                          fontWeight: 800,
                          bgcolor: "background.paper",
                          borderBottom: "2px solid",
                          borderColor: "divider",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col.label}
                      </TableCell>
                    )
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {!hasData ? (
                <TableRow>
                  <TableCell
                    colSpan={defaultColumns.filter((c) => visibleCols[c.id]).length || 1}
                    sx={{ py: 4 }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                        Pas de transactions
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Aucune donnée à afficher pour cette période / filtre.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((tx) => {
                  const rowCurrency = tx?.currency ?? reportCurrency ?? "";
                  const chip = getStatusChipProps(tx?.status);

                  return (
                    <TableRow
                      key={tx.secret_code}
                      hover
                      onClick={() => goToDetails(tx)}
                      sx={{
                        cursor: "pointer",
                        "&:hover td": { bgcolor: "action.hover" },
                        transition: "background-color 120ms ease",
                      }}
                    >
                      {visibleCols.secret_code && (
                        <TableCell sx={{ fontWeight: 700 }}>{tx.secret_code}</TableCell>
                      )}
                      {visibleCols.sender_name && <TableCell>{tx.sender_name}</TableCell>}
                      {visibleCols.receiver_name && <TableCell>{tx.receiver_name}</TableCell>}
                      {visibleCols.amount && (
                        <TableCell sx={{ fontWeight: 700 }}>
                          {fmtMoney(tx.amount, rowCurrency)}
                        </TableCell>
                      )}
                      {visibleCols.fee && (
                        <TableCell sx={{ fontWeight: 700 }}>
                          {fmtMoney(tx.fee ?? 0, rowCurrency)}
                        </TableCell>
                      )}
                      {visibleCols.status && (
                        <TableCell>
                          <Chip
                            label={chip.label}
                            color={chip.color}
                            variant={chip.variant}
                            size="small"
                            sx={{ fontWeight: 800, borderRadius: 2, px: 0.5 }}
                          />
                        </TableCell>
                      )}
                      {visibleCols.sender_agency_name && (
                        <TableCell>{tx.sender_agency_name ?? "—"}</TableCell>
                      )}
                      {visibleCols.sender_location_name && (
                        <TableCell>{tx.sender_location_name ?? "—"}</TableCell>
                      )}
                      {visibleCols.receiver_location_name && (
                        <TableCell>{tx.receiver_location_name ?? "—"}</TableCell>
                      )}
                      {visibleCols.created_by_name && (
                        <TableCell>{tx.created_by_name ?? "—"}</TableCell>
                      )}
                      {visibleCols.created_at && (
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatTxDate(tx.created_at, reportTimezone)}
                        </TableCell>
                      )}

                      {visibleCols.actions && (
                        <TableCell
                          onClick={(e) => e.stopPropagation()}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          <Tooltip title="Voir les détails">
                            <Button
                              onClick={() => goToDetails(tx)}
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityIcon />}
                              sx={{ borderRadius: 2, fontWeight: 700 }}
                            >
                              Détails
                            </Button>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </Paper>
  );
};

export default TransactionTable;